/**
 * net/HttpGateway.ts — a rede real do receptor web (fases 4–5, dev-pleno).
 *
 * Implementa o `ReceptorGateway` CONGELADO no brief 02 §"Contrato de interface".
 * Nada aqui sabe o que é DOM: a casca do dev-junior fala com esta interface, não
 * com `fetch`. Se um dia a API mudar, muda um arquivo só.
 *
 * TRÊS REGRAS QUE NÃO SÃO NEGOCIÁVEIS (e que custaram bug no repo antes):
 *  1. **`304` é sucesso, não erro.** `fetchQueue` devolve `null` e o loop continua
 *     exatamente igual — sem reposicionar, sem piscar (PLAYER.md §4.1.2).
 *  2. **A mídia vem COM o device token.** `GET /media/:id/file` sem credencial dá
 *     401 e a TV nunca exibe nada (comentário em `media/routes.ts:328`).
 *  3. **O receptor decide pelo `code`, nunca pela `message`** (contrato-base §2).
 *
 * ponytail: `fetch`/`EventSource` nativos e injetáveis. Se o dia vier um
 * `retry/wrapper` de rede, é a implementação de `HttpGateway` que troca, não a
 * interface.
 */
import type { Ad, ID, MediaAsset, PlaybackItem } from '../../src/domain/types';
import { GatewayError, codeFromErrorEnvelope } from './errors';
import { backoffDelayMs } from './backoff';
import { SyncedClock, systemSleeper, type Sleeper, type Clock } from './clock';
import { DEFAULT_API_BASE_URL, resolveApiBaseUrl } from './config';

export { DEFAULT_API_BASE_URL, resolveApiBaseUrl };

/** Payload de 200 do `GET /screens/:id/queue` (API-CONTRACT §4.1). */
export interface QueuePayload {
  screenId: ID;
  version: string;
  generatedAt: string;
  serverTime: string;
  rotationEpochSeconds: number;
  items: PlaybackItem[];
  ads: Ad[];
}

/** Resposta do heartbeat que o PLAYER §5.1 usa. */
export interface HeartbeatResult {
  serverTime: string;
  heartbeatIntervalSeconds: number;
  queueOutdated: boolean;
  desiredQueueVersion: string;
  ack: true;
  id: ID;
  name: string;
  status: 'online' | 'offline';
}

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

/** Filtros de SSE que o receptor tem o direito de observar. */
export interface RealtimeSubscription {
  /** `true` = o stream conectou pelo menos uma vez nesta sessão de subscribe. */
  connected(): boolean;
  /** Fecha a conexão e CANCELA o ciclo de reconexão. */
  close(): void;
}

/** `EventSource` que o gateway precisa — o mínimo, para injetar um dublê. */
export interface EventSourceLike {
  onopen: ((event: unknown) => void) | null;
  onerror: ((event: unknown) => void) | null;
  onmessage: ((event: { data?: string }) => void) | null;
  addEventListener(type: string, listener: (event: { data?: string }) => void): void;
  close(): void;
}

export interface HttpGatewayOptions {
  deviceToken: string;
  screenId: ID;
  baseUrl?: string;
  appVersion?: string;
  platform?: 'web' | 'android' | 'androidtv';
  clock?: Clock;
  fetchImpl?: FetchLike;
  /** Injetável para testar o caminho sem browser (§4.5 do brief 02). */
  eventSourceFactory?: (url: string) => EventSourceLike;
  /** `EventSource` real só existe no browser; no Node entra um stub de teste. */
  onLog?: (message: string, detail?: unknown) => void;
}

/**
 * D6 — CORREÇÃO DE RELÓGIO. Se `|serverTime − now| > 120 s`, aplica o offset.
 *
 * Sem isso, "até 22:00" numa TV Box com relógio errado vira "até 05:00"
 * (PLAYER.md §4.4). O offset é aplicado em cima do relógio do dispositivo, não
 * substituído: um relógio que anda devagar continua sendo corrigido a cada
 * heartbeat.
 */
export const CLOCK_SKEW_THRESHOLD_MS = 120_000;

export class HttpGateway {
  private readonly deviceToken: string;
  private readonly screenId: ID;
  private readonly baseUrl: string;
  private readonly appVersion: string;
  private readonly platform: 'web' | 'android' | 'androidtv';
  /** D6 vive AQUI e só aqui: o relógio corrigido que o player consome. */
  private readonly clock: SyncedClock;
  private readonly sleeper: Sleeper;
  private readonly doFetch: FetchLike;
  private readonly eventSourceFactory: (url: string) => EventSourceLike;
  private readonly log: (message: string, detail?: unknown) => void;

  constructor(options: HttpGatewayOptions) {
    this.deviceToken = options.deviceToken;
    this.screenId = options.screenId;
    this.baseUrl = (options.baseUrl ?? resolveApiBaseUrl()).replace(/\/+$/, '');
    this.appVersion = options.appVersion ?? '0.0.0-web';
    this.platform = options.platform ?? 'web';
    this.clock = new SyncedClock(options.clock);
    this.sleeper = systemSleeper;
    this.doFetch = options.fetchImpl ?? ((input, init) => fetch(input, init));
    // O `EventSource` nativo declara `onopen/onerror` com `Event`, o que é
    // estruturalmente incompatível com o dublê minimalista que os testes injetam.
    // O cast fica AQUI, na fronteira com o browser — o resto do arquivo só conhece
    // `EventSourceLike`.
    this.eventSourceFactory =
      options.eventSourceFactory ??
      ((url: string) => new EventSource(url) as unknown as EventSourceLike);
    this.log = options.onLog ?? (() => {});
  }

  /** Relógio JÁ CORRIGIDO — é o que o player e a reavaliação offline usam. */
  now(): Date {
    return this.clock.nowDate();
  }

  /**
   * O relógio com a correção D6 aplicada. A casca (dev-junior) usa este objeto
   * para avaliar `isAdActiveAt` offline: injetar o relógio aqui é o que torna o
   * critério 7 ("sai às 22:00 com a internet desligada") testável.
   */
  get syncedClock(): SyncedClock {
    return this.clock;
  }

  /** Deslocamento aplicado (ms). Diagnóstico/teste; nunca exibido na TV. */
  get clockOffset(): number {
    return this.clock.offset;
  }

  private url(path: string): string {
    // Relative paths are API-relative; absolute media URLs must keep their origin.
    try {
      const url = /^[a-z][a-z0-9+.-]*:|^[/\\]{2}/i.test(path.trim())
        ? new URL(path, `${this.baseUrl}/`)
        : new URL(`${this.baseUrl}/${path.replace(/^\//, '')}`);
      if (!['http:', 'https:'].includes(url.protocol) ||
          url.origin !== new URL(this.baseUrl).origin || url.username || url.password) {
        throw new Error('Untrusted URL');
      }
      return url.href;
    } catch {
      throw new GatewayError('BAD_RESPONSE', 'URL de mídia não confiável.');
    }
  }

  private authHeaders(extra: Record<string, string> = {}): Record<string, string> {
    return { Authorization: `Bearer ${this.deviceToken}`, ...extra };
  }

  /**
   * Lê o envelope de erro do contrato `{error:{code,message}}` e vira `GatewayError`.
   * Envelope ausente (HTML de proxy, 500 cru) cai no status — nunca na raspagem
   * de texto.
   */
  private async toGatewayError(response: Response): Promise<GatewayError> {
    let envelopeCode: string | undefined;
    let message = `HTTP ${response.status}`;
    try {
      const body = (await response.clone().json()) as { error?: { code?: string; message?: string } };
      if (body && typeof body === 'object' && body.error) {
        if (typeof body.error.code === 'string') envelopeCode = body.error.code;
        if (typeof body.error.message === 'string') message = body.error.message;
      }
    } catch {
      // Corpo não-JSON: seguimos com status + mensagem genérica.
    }
    const code = codeFromErrorEnvelope(envelopeCode, response.status);
    this.log('gateway erro', { status: response.status, envelopeCode, code });
    return new GatewayError(code, message, response.status);
  }

  /**
   * `GET /screens/:id/queue` com `If-None-Match`.
   *
   * `null` = `304` (nada mudou, continue o loop). É o retorno de SUCESSO mais
   * importante do contrato: tratá-lo como erro fazia a TV piscar a cada ciclo.
   */
  async fetchQueue(knownVersion?: string): Promise<QueuePayload | null> {
    const response = await this.request(`/screens/${this.screenId}/queue`, {
      method: 'GET',
      // O backend compara o ETag de forma EXATA (contrato §5.2): aspas incluídas.
      headers: this.authHeaders(
        knownVersion ? { 'If-None-Match': `"${knownVersion}"` } : {},
      ),
    });

    if (response.status === 304) {
      this.log('queue 304 — nada mudou');
      return null;
    }
    if (!response.ok) {
      throw await this.toGatewayError(response);
    }
    const payload = (await response.json()) as QueuePayload;
    if (!payload || typeof payload.version !== 'string' || !Array.isArray(payload.items)) {
      // 200 sem payload de fila = contrato furado (ou proxy com JSON de outra coisa).
      throw new GatewayError('BAD_RESPONSE', 'Resposta de fila inesperada.', response.status);
    }
    this.syncClock(payload.serverTime);
    return payload;
  }

  /**
   * Baixa o arquivo de mídia COM o device token e confere os bytes antes de
   * devolver. Um `Blob` que não abre na TV é pior que um item pulado (PLAYER §6).
   *
   * ponytail: a checagem de magic bytes + tamanho é feita AQUI, no limite, e não
   * no player — é o único lugar onde o arquivo é visto cru.
   */
  async fetchMediaBytes(media: MediaAsset): Promise<Blob> {
    const response = await this.request(media.url, {
      method: 'GET',
      headers: this.authHeaders(),
    });
    if (!response.ok) {
      throw await this.toGatewayError(response);
    }
    const blob = await response.blob();

    const expected = media.sizeBytes;
    if (typeof expected === 'number' && expected > 0 && blob.size !== expected) {
      // Download truncado: marca como indisponível em vez de exibir lixo.
      throw new GatewayError(
        'BAD_RESPONSE',
        `Mídia ${media.id} com tamanho inesperado (${blob.size} != ${expected}).`,
        response.status,
      );
    }
    if (!(await looksLikeMedia(blob, media.kind))) {
      throw new GatewayError('BAD_RESPONSE', `Mídia ${media.id} não tem formato válido.`, response.status);
    }
    return blob;
  }

  /**
   * Heartbeat (§3.6). É ele que dita o intervalo do próximo, diz se a fila está
   * velha e carrega o `serverTime` da correção de relógio.
   */
  async heartbeat(state: { queueVersion?: string; currentAdId?: string } = {}): Promise<HeartbeatResult> {
    const response = await this.request(`/screens/${this.screenId}/heartbeat`, {
      method: 'POST',
      headers: this.authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({
        screenId: this.screenId,
        ...(state.queueVersion ? { queueVersion: state.queueVersion } : {}),
        platform: this.platform,
        appVersion: this.appVersion,
        ...(state.currentAdId ? { currentAdId: state.currentAdId } : {}),
        // O relógio JÁ CORRIGIDO: mandar `occurredAt` cru seria telemetria falsa.
        occurredAt: this.now().toISOString(),
      }),
    });
    if (!response.ok) {
      throw await this.toGatewayError(response);
    }
    const result = (await response.json()) as HeartbeatResult;
    if (!result || (result.status !== 'online' && result.status !== 'offline')) {
      throw new GatewayError('BAD_RESPONSE', 'Status de heartbeat inesperado.', response.status);
    }
    this.syncClock(result.serverTime);
    return result;
  }

  /**
   * Aplica o offset D6 a partir de um `serverTime` do servidor.
   * Só corrige acima de 120 s: abaixo disso, ajustar é só adicionar jitter ao relógio.
   */
  private syncClock(serverTime: string | undefined): void {
    // A regra do limiar (120 s) está em `SyncedClock.sync` — uma implementação só.
    if (this.clock.sync(serverTime)) {
      this.log('relógio corrigido (D6)', { offsetMs: this.clock.offset });
    }
  }

  /**
   * `GET /realtime/stream?device=` (D2). `EventSource` não manda header, e o
   * backend aceita a query de propósito.
   *
   * A ASSINATURA É DELIBERADAMENTE MÍNIMA: devolve `() => void` e avisa por
   * callback quando o stream não conectou em 30 s. Quem chama decide o que fazer
   * (o PLAYER §5.2 manda seguir só com polling + heartbeat) — a decisão é da casca,
   * não da rede.
   */
  subscribeQueueUpdates(
    onChange: (version: string) => void,
    onUnavailable?: (reason: 'timeout' | 'closed') => void,
    options: { connectTimeoutMs?: number } = {},
  ): () => void {
    const connectTimeoutMs = options.connectTimeoutMs ?? 30_000;
    const url = `${this.url('/realtime/stream')}?device=${encodeURIComponent(this.deviceToken)}`;

    let source: EventSourceLike | null = null;
    let attempt = 0;
    let everConnected = false;
    let closed = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
    let connectTimer: ReturnType<typeof setTimeout> | undefined;

    const clearConnectTimer = (): void => {
      if (connectTimer !== undefined) {
        clearTimeout(connectTimer);
        connectTimer = undefined;
      }
    };

    const connect = (): void => {
      if (closed) {
        return;
      }
      source = this.eventSourceFactory(url);
      source.onopen = () => {
        clearConnectTimer();
        everConnected = true;
        attempt = 0;
      };
      source.onmessage = event => {
        const raw = event.data;
        if (typeof raw !== 'string' || raw === '') {
          return;
        }
        const parsed = safeParseEvent(raw);
        if (parsed === null) {
          return;
        }
        // REGRA DE OURO (§7.3): o evento diz QUE mudou e QUAL versão esperar.
        // O corpo vem no `GET /queue` — nunca no evento.
        if (parsed.type === 'queue.updated') {
          const payloadScreen = parsed.payload['screenId'];
          if (typeof payloadScreen === 'string' && payloadScreen !== this.screenId) {
            return; // evento de outra tela da mesma conta: não é nosso.
          }
          const version = parsed.payload['version'];
          if (typeof version === 'string') {
            onChange(version);
          } else {
            onChange('');
          }
        }
      };
      source.addEventListener('queue.updated', source.onmessage);
      source.onerror = () => {
        source?.close();
        source = null;
        clearConnectTimer();
        if (closed) {
          return;
        }
        if (!everConnected) {
          // Primeiro tuy: 30 s sem conectar ⇒ modo só-polling (PLAYER §5.2).
          onUnavailable?.('timeout');
        }
        scheduleReconnect();
      };

      clearConnectTimer();
      connectTimer = setTimeout(() => {
        if (!everConnected && !closed) {
          // Não connectou: o proxy corporativo bloqueou SSE. Não insistimos mais
          // que 30 s — polling + heartbeat seguram o produto.
          onUnavailable?.('timeout');
        }
      }, connectTimeoutMs);
    };

    const scheduleReconnect = (): void => {
      if (closed || reconnectTimer !== undefined) {
        return;
      }
      const delay = backoffDelayMs(attempt);
      attempt += 1;
      reconnectTimer = setTimeout(() => {
        reconnectTimer = undefined;
        connect();
      }, delay);
    };

    connect();

    return () => {
      closed = true;
      clearConnectTimer();
      if (reconnectTimer !== undefined) {
        clearTimeout(reconnectTimer);
        reconnectTimer = undefined;
      }
      source?.close();
      source = null;
      onUnavailable?.('closed');
    };
  }

  /**
   * `fetch` com o tratamento de rede centralizado: falha de DNS/socket vira
   * `GatewayError('NETWORK')` (que NUNCA mostra erro na TV), não `TypeError` cru.
   */
  private async request(path: string, init: RequestInit): Promise<Response> {
    const url = this.url(path);
    try {
      return await this.doFetch(url, {
        ...init,
        redirect: 'error', // Never forward device credentials through redirects.
        signal: AbortSignal.timeout(30_000),
      });
    } catch (error) {
      this.log('falha de rede', { path, error: String(error) });
      throw new GatewayError('NETWORK', 'Sem conexão com o servidor.');
    }
  }
}

type ParsedEvent = { type: string; payload: Record<string, unknown> };

function safeParseEvent(raw: string): ParsedEvent | null {
  try {
    const parsed = JSON.parse(raw) as Partial<ParsedEvent>;
    if (!parsed || typeof parsed.type !== 'string') {
      return null;
    }
    const payload =
      parsed.payload && typeof parsed.payload === 'object'
        ? (parsed.payload as Record<string, unknown>)
        : {};
    return { type: parsed.type, payload };
  } catch {
    return null; // lixo no stream não pode derrubar o player.
  }
}

/**
 * Confere os magic bytes do arquivo baixado. A TV não pode descobrir que o
 * download veio corrompido quando o `<img>` já está no ar (PLAYER §6).
 *
 * `....ftyp` é MP4: o box fica nos bytes 4..8, então procuramos a assinatura em
 * vez de assumir offset — arquivos de câmera variam.
 */
export async function looksLikeMedia(blob: Blob, kind: 'image' | 'video'): Promise<boolean> {
  const head = new Uint8Array(await blob.slice(0, 32).arrayBuffer());
  if (head.length < 4) {
    return false;
  }
  if (kind === 'image') {
    const isJpeg = head[0] === 0xff && head[1] === 0xd8 && head[2] === 0xff;
    const isPng =
      head[0] === 0x89 && head[1] === 0x50 && head[2] === 0x4e && head[3] === 0x47;
    const isWebp =
      head[0] === 0x52 && head[1] === 0x49 && head[2] === 0x46 && head[3] === 0x46;
    return isJpeg || isPng || isWebp;
  }
  for (let i = 4; i <= head.length - 4; i += 1) {
    if (
      head[i] === 0x66 &&
      head[i + 1] === 0x74 &&
      head[i + 2] === 0x79 &&
      head[i + 3] === 0x70
    ) {
      return true; // "ftyp"
    }
  }
  return false;
}
